import { InjectionToken, Signal, signal } from '@angular/core';
import { FormioForm, ExtendedComponentSchema } from '@formio/angular';

import { mapFormioComponentsDeep } from './map-formio-components-deep';

/**
 * Rewrites the display format tokens of Form.io `datetime` components from ISO
 * order (`yyyy-MM-dd`) to US order (`MM/dd/yyyy`). Time-enabled datetimes become
 * `MM/dd/yyyy, hh:mm a` — a comma separates the date from the time (the time token
 * itself is unchanged).
 *
 * Invariant: only the display tokens `format` and `widget.format` are touched.
 * `dateFormat`, `widget.dateFormat`, `saveAs`, and every submission value are
 * left unchanged, so the persisted value stays bare ISO and no date parsing is
 * introduced. Non-`datetime` components and formats without the ISO token are
 * returned unchanged. Only `type === 'datetime'` is in scope.
 */

/** US date-format tokens (Form.io/flatpickr — case-sensitive). */
export const US_DATE_FORMAT = {
  isoDateToken: 'yyyy-MM-dd',
  usDateToken: 'MM/dd/yyyy',
} as const;

/**
 * Gate for the US date-format override, resolved by each application's composition
 * layer from its own session: the EMR provides it from `AuthStore`, the authenticated
 * portal from `PortalSessionStore`. Absence defaults safely to off (the renderer reads
 * it optionally). A route that gates per-form instead of per-session (public intake)
 * overrides it with {@link US_DATE_FORMAT_DISABLED} so the session value can't leak in.
 */
export const US_DATE_FORMAT_ENABLED = new InjectionToken<Signal<boolean>>('US_DATE_FORMAT_ENABLED');

/** A stable always-off gate signal for composition layers that must force the override off. */
export const US_DATE_FORMAT_DISABLED: Signal<boolean> = signal(false);

/**
 * Replace the ISO date token with the US date token; no-op if absent. When
 * `withTimeSeparator` and a time portion follows (date token + space), the
 * separator becomes `, ` so a time-enabled datetime reads `MM/dd/yyyy, hh:mm a`.
 * Date-only (or no trailing time) stays `MM/dd/yyyy`.
 */
function toUSToken(format: string, withTimeSeparator: boolean): string {
  if (withTimeSeparator) {
    const withComma = format.replace(`${US_DATE_FORMAT.isoDateToken} `, `${US_DATE_FORMAT.usDateToken}, `);
    if (withComma !== format) return withComma;
  }
  return format.replace(US_DATE_FORMAT.isoDateToken, US_DATE_FORMAT.usDateToken);
}

function overrideComponent(component: ExtendedComponentSchema): ExtendedComponentSchema {
  if (component.type !== 'datetime') return component;

  // A time-only field (`enableDate: false`) has no date to reformat, and Form.io's
  // DateTime constructor strips the date via `/yyyy-MM-dd /` — which only matches the
  // ISO token. Leave such components untouched so that strip still fires; converting
  // the token would defeat it and wrongly keep a date. (enableDate defaults to true.)
  const enableDate = (component as { enableDate?: boolean }).enableDate;
  if (enableDate === false) return component;

  const widget =
    component.widget && typeof component.widget === 'object'
      ? (component.widget as Record<string, unknown>)
      : undefined;
  const widgetFormat = widget && typeof widget.format === 'string' ? (widget.format as string) : undefined;

  // Form.io 5.x rebuilds `widget.format` from `component.format`, so `component.format`
  // is authoritative. Some authored schemas (e.g. the default event form) carry the
  // token only on `widget.format` — the override must seed `component.format` from it so
  // the rebuild keeps US order, even when `widget.format` is already US (otherwise the
  // absent top-level format falls back to Form.io's ISO default).
  const currentFormat = typeof component.format === 'string' ? component.format : widgetFormat;
  if (typeof currentFormat !== 'string') return component;

  // Only insert the date/time comma when the time half is actually rendered.
  // If `enableTime: false`, Form.io strips a trailing ` hh:mm a`; an inserted comma
  // would be orphaned as `MM/dd/yyyy,`. (enableTime defaults to true.)
  const withTimeSeparator = (component as { enableTime?: boolean }).enableTime !== false;
  const usFormat = toUSToken(currentFormat, withTimeSeparator);
  const componentFormatSatisfied = component.format === usFormat;
  const widgetSatisfied = widgetFormat === undefined || widgetFormat === usFormat;
  if (componentFormatSatisfied && widgetSatisfied) return component;

  const patched: ExtendedComponentSchema = { ...component, format: usFormat };
  if (widget) {
    patched.widget = { ...widget, format: usFormat };
  }
  return patched;
}

/**
 * Returns a new form schema with every `datetime` component's display format
 * rewritten to US order. Deep-walks panels, `columns[].components`, and datagrid
 * `rows[][].components`. Does not mutate the input.
 */
export function applyDateFormatOverride(form: FormioForm): FormioForm {
  if (!form || !Array.isArray(form.components)) return form;
  return {
    ...form,
    components: mapFormioComponentsDeep(form.components, overrideComponent),
  };
}
